import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellOff, CheckCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from '@components/ui';
import { cn } from '@lib/utils/cn';
import { notificationsApi, type NotificationDoc } from '../api/notificationsApi';
import { useNotificationsList } from '../hooks/useNotificationsList';
import { formatRelativeTime, getNotificationIcon } from './notificationIcons';
import { resolveNotificationRoute } from '../lib/resolveNotificationRoute';

export interface NotificationDropdownProps {
  /** Called after the user picks "View all" / a deep-link / mark-all so the popover closes. */
  onClose: () => void;
}

const PREVIEW_LIMIT = 10;

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const listQuery = useNotificationsList({ limit: PREVIEW_LIMIT });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: (res) => {
      toast.success(
        t('notifications.mark_all_read_success', {
          defaultValue: isRtl
            ? `تم تعليم ${res.modified} كمقروء`
            : `Marked ${res.modified} as read`,
        }),
      );
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => {
      toast.error(t('common.error', { defaultValue: isRtl ? 'حدث خطأ' : 'Something went wrong' }));
    },
  });

  const items = listQuery.data?.data ?? [];

  function handleRowClick(notif: NotificationDoc) {
    if (!notif.read) {
      markReadMutation.mutate(notif._id);
    }
    onClose();
    // Derive the correct web route from entity_type — the stored deep_link is
    // shared with the mobile app and often points at a mobile/stale path that
    // 404s on web (notes7 #3).
    navigate(resolveNotificationRoute(notif));
  }

  return (
    <div
      role="dialog"
      aria-label={t('notifications.bell_label', { defaultValue: isRtl ? 'الإشعارات' : 'Notifications' })}
      className="w-[22rem] max-w-[calc(100vw-1rem)] rounded-md border border-border bg-surface shadow-elevated overflow-hidden animate-slide-in-up"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-bg-muted/40">
        <h3 className="text-sm font-semibold">
          {t('notifications.bell_label', { defaultValue: isRtl ? 'الإشعارات' : 'Notifications' })}
        </h3>
        <button
          type="button"
          onClick={() => markAllMutation.mutate()}
          disabled={items.every((n) => n.read) || markAllMutation.isPending}
          className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline disabled:opacity-50 disabled:no-underline"
        >
          <CheckCheck className="h-3.5 w-3.5" />
          {t('notifications.mark_all_read', {
            defaultValue: isRtl ? 'تعليم الكل كمقروء' : 'Mark all as read',
          })}
        </button>
      </div>

      {/* Body */}
      <div className="max-h-[28rem] overflow-y-auto">
        {listQuery.isLoading ? (
          <div className="py-10 grid place-items-center">
            <Spinner />
          </div>
        ) : listQuery.isError ? (
          <div className="py-10 text-center text-sm text-danger px-4">
            {(listQuery.error as Error)?.message ?? 'Failed to load'}
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 px-4 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-bg-muted grid place-items-center mb-3">
              <BellOff className="h-6 w-6 text-fg-muted" />
            </div>
            <p className="text-sm font-medium text-fg">
              {t('notifications.empty_state', {
                defaultValue: isRtl ? 'لا توجد إشعارات' : 'No notifications yet',
              })}
            </p>
            <p className="text-xs text-fg-muted mt-1">
              {isRtl ? 'سنخبرك عند وصول أي شيء جديد.' : "We'll notify you when something arrives."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border" role="list">
            {items.map((notif) => (
              <NotificationRow
                key={notif._id}
                notif={notif}
                isRtl={isRtl}
                t={t}
                onClick={() => handleRowClick(notif)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border bg-bg-muted/40">
        <button
          type="button"
          onClick={() => {
            onClose();
            navigate('/notifications');
          }}
          className="w-full px-4 py-2.5 text-sm font-medium text-brand hover:bg-brand-muted/30 transition-colors flex items-center justify-center gap-2"
        >
          {t('notifications.view_all', {
            defaultValue: isRtl ? 'عرض كل الإشعارات' : 'View all notifications',
          })}
          <ArrowRight className={cn('h-4 w-4', isRtl && 'rotate-180')} />
        </button>
      </div>
    </div>
  );
}

interface RowProps {
  notif: NotificationDoc;
  isRtl: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  onClick: () => void;
}

function NotificationRow({ notif, isRtl, t, onClick }: RowProps) {
  const { icon: Icon, tone, bg } = getNotificationIcon(notif.type);
  const title = isRtl ? notif.title_ar : notif.title_en;
  const body = isRtl ? notif.body_ar : notif.body_en;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full text-start px-4 py-3 hover:bg-bg-muted/50 transition-colors flex gap-3 items-start',
          !notif.read && 'bg-brand-muted/10',
        )}
      >
        <div className={cn('h-9 w-9 shrink-0 rounded-full grid place-items-center', bg)}>
          <Icon className={cn('h-4.5 w-4.5', tone)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className={cn(
                'text-sm leading-snug truncate',
                notif.read ? 'text-fg-muted font-normal' : 'text-fg font-semibold',
              )}
            >
              {title}
            </p>
            {!notif.read && (
              <span
                aria-hidden
                className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand"
                title={isRtl ? 'غير مقروء' : 'Unread'}
              />
            )}
          </div>
          {body && (
            <p className="text-xs text-fg-muted mt-0.5 line-clamp-1">{body}</p>
          )}
          <p className="text-[11px] text-fg-subtle mt-1">
            {formatRelativeTime(notif.createdAt, isRtl, t)}
          </p>
        </div>
      </button>
    </li>
  );
}
